import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CircleCheck, Download, Loader2, RefreshCw, Store, Users, type LucideIcon } from 'lucide-react';
import { TableActionButton, TableActionGroup, TableActionLink } from '@channelplay/table-actions';
import Modal from '../Modal';
import { FormErrorBanner } from '../FormErrorBanner';
import { dataHygieneDashboardAPI } from '../../services/api';
import { useHeldPage } from '../../navigation/useHeldPage';
import type { MenuItem } from '../../stores/menuStore';
import { downloadTableXlsx } from '../../utils/tableXlsx';
import { SearchBox } from './DashboardPrimitives';
import { useDashboardList } from './useDashboardList';
import {
  COULD_NOT_CHECK,
  EMPLOYEES_PAGE,
  EMPLOYEE_CHECKS,
  HYGIENE_GROUPS,
  HYGIENE_SIDES,
  MARKED_LABEL,
  MARKED_SLUG,
  MARK_ANSWER,
  MARK_HOVER,
  MARK_QUESTION,
  ROW_LIMIT,
  STORES_PAGE,
  STORE_CHECKS,
  USER_ROLES_PAGE,
  activeChip,
  blockHeading,
  checkFromSlug,
  checksOf,
  countLine,
  employeesInRoles,
  employeeFixLink,
  employeeFixPage,
  employeeMarkRequest,
  employeeMatchesSearch,
  employeeSheet,
  employeeWrongText,
  firstBlocks,
  fmtCheckedTime,
  fmtInt,
  fmtMarkedDate,
  hygieneFileName,
  isDuplicateCheck,
  markErrorText,
  markedSideFromSlug,
  missesField,
  nothingToFixLine,
  readEmployees,
  readStores,
  roleFilterNote,
  showsStatus,
  shownCheck,
  storeBlocks,
  storeFixLink,
  storeMarkRequest,
  storeMatchesSearch,
  storeSheet,
  storeWrongText,
  type DataHygieneEmployees,
  type EmployeeCheckInfo,
  type HygieneCheckInfo,
  type HygieneCheckStatus,
  type HygieneEmployeeCheck,
  type HygieneEmployeeRow,
  type HygieneFieldCount,
  type HygieneMarkedEmployee,
  type HygieneMarkedStore,
  type HygieneSide,
  type HygieneStoreCheck,
  type HygieneStoreRow,
  type MarkRequest,
  type StoreBlock,
  type StoreCheckInfo,
} from './dataHygiene';

/**
 * Dashboard → Data Hygiene tab.
 *
 * One screen that lists the mistakes and gaps in the records this team enters itself,
 * so they can be fixed: stores entered twice, stores and people with a value missing,
 * and values that cannot be right. No record is changed here. Each row links to the page
 * where its record is fixed, and a row that is right as it is can be marked OK: it
 * leaves its list until what was flagged changes (the API's rule).
 *
 * Things a reader must know before touching it:
 *   1. THE OPEN LIST IS THE ADDRESS. Which check is open is read from ?list=<slug> on
 *      every render (useDashboardList) and never copied into state. The open side,
 *      Stores or Employees, follows from that check. No slug, one that is not a check,
 *      or a check with nothing in it, shows the side's first check that has something.
 *   2. THE TWO SIDES LOAD APART AND FAIL APART. Stores and Employees are two requests,
 *      both sent on arrival, so the switch shows both counts and a slow read of the
 *      people (it needs the 1Office roster) never holds up the store lists. A side that
 *      fails says so on that side only.
 *   3. REFRESH ASKS FOR BOTH SIDES AGAIN. Each side keeps its count and its rows on
 *      screen until its own new answer lands, so a search someone typed survives the
 *      Refresh that follows a fix. Until both have landed, one count can be newer than
 *      the other. Refresh waits while either side is still on its way, so the slow read
 *      of the people is never sent twice at once.
 *   4. ACCESS IS THE API'S ANSWER. The dashboard covers the whole project, and only the
 *      server knows whether the viewer sees all of it. A 403 from either side is a
 *      state of the tab, a quiet panel, not an error and not a sign-out. The panel can
 *      ask again: access given a moment ago counts from the next request.
 *   5. A NUMBER THAT IS NOT KNOWN IS NEVER 0. A check the server could not run shows a
 *      dash. A side that has not loaded shows no count on the switch.
 *   6. THE MONTH DOES NOT APPLY, AND THE ROLE FILTER NARROWS THE EMPLOYEES SIDE ONLY.
 *      The checks read the records as they stand now. The page hands over its shared
 *      Role filter (`roles`, which opens on the roles that work at a store); it narrows
 *      the Employees counts and lists in the browser, except "No role", whose people
 *      hold no role to match (see employeesInRoles). The store checks are about the
 *      store records and ignore it. While the page is still working out the store-level
 *      default (`rolesPending`), the Employees side waits rather than show everyone
 *      first; the Stores side does not wait. The tab says in a few words which figures
 *      the filter narrows.
 *   7. MARK AS OK ANSWERS WITH THE SIDE. Marking a row (after an in-page confirm) and
 *      undoing a mark each answer with that side as it now stands, which replaces what
 *      is held, so the row leaves (or comes back) with its counts at once. The rows
 *      marked OK are a list of their own, opened by ?list=marked-ok (people-marked-ok on
 *      the Employees side) from the "Marked OK (N)" chip beside the switch.
 */

const ACCESS_NEEDED = 'Data Hygiene covers the whole project. Ask an admin for whole-project access.';

const SIDE_ICON: Record<HygieneSide, LucideIcon> = { stores: Store, employees: Users };

const CARD = 'rounded-2xl border border-gray-200 bg-white p-4 shadow-sm';
const PILL = 'inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-primary-200 bg-white px-3 py-1.5 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-50';

const TH = 'whitespace-nowrap px-4 py-3 text-left text-xs font-semibold text-gray-500';
// Padding apart from colour: a cell that holds two lines in two colours takes CELL and
// colours each line itself, so no cell ever carries two text colours.
const CELL = 'px-4 py-3';
const TD = `${CELL} text-gray-700`;

// ── Loading one side ─────────────────────────────────────────────────────────

type SideState<T> =
  /** Not asked for yet. */
  | { status: 'idle' }
  | { status: 'loading' }
  /** `refreshing`: asked for again, with these rows left on screen meanwhile. */
  | { status: 'ready'; data: T; refreshing: boolean }
  | { status: 'failed' }
  /** 403: the viewer does not see the whole project. */
  | { status: 'denied' };

const IDLE = { status: 'idle' } as const;
const LOADING = { status: 'loading' } as const;
const FAILED = { status: 'failed' } as const;
const DENIED = { status: 'denied' } as const;

type Answer = { data?: { data?: unknown } | null };

const askStores = (projectId: number): Promise<Answer> => dataHygieneDashboardAPI.getStores(projectId);
const askEmployees = (projectId: number): Promise<Answer> => dataHygieneDashboardAPI.getEmployees(projectId);

/**
 * One side's answer for the project on screen.
 *
 * What is held is kept WITH the project it was asked for, so an answer for another
 * project is never drawn: after a project change the side simply reads as not asked
 * for yet. `ticket` lets only the newest request land.
 */
function useSide<T>(projectId: number | null, call: (projectId: number) => Promise<Answer>, read: (raw: unknown) => T | null) {
  const [held, setHeld] = useState<{ projectId: number | null; state: SideState<T> }>({ projectId, state: IDLE });
  const ticket = useRef(0);
  const state: SideState<T> = held.projectId === projectId ? held.state : IDLE;
  // The project on screen now, for an answer that lands after the person has moved on.
  const onScreen = useRef(projectId);
  onScreen.current = projectId;

  /**
   * Hold an answer that came from a mark or an undo (the side as it now stands). It
   * wins over any read still on its way, which was asked before the change. False when
   * the answer cannot be read, or is for a project no longer on screen: nothing changes.
   */
  const put = useCallback((pid: number, raw: unknown): boolean => {
    const data = read(raw);
    if (!data || onScreen.current !== pid) return false;
    ticket.current += 1;
    setHeld({ projectId: pid, state: { status: 'ready', data, refreshing: false } });
    return true;
  }, [read]);

  const ask = useCallback(() => {
    if (projectId == null) return;
    const pid = projectId;
    const mine = ++ticket.current;
    setHeld((prev) => {
      const was: SideState<T> = prev.projectId === pid ? prev.state : IDLE;
      return { projectId: pid, state: was.status === 'ready' ? { ...was, refreshing: true } : LOADING };
    });
    call(pid).then(
      (res) => {
        if (ticket.current !== mine) return;
        const data = read(res?.data?.data);
        setHeld({ projectId: pid, state: data ? { status: 'ready', data, refreshing: false } : FAILED });
      },
      (err: unknown) => {
        if (ticket.current !== mine) return;
        const status = (err as { response?: { status?: number } })?.response?.status;
        setHeld({ projectId: pid, state: status === 403 ? DENIED : FAILED });
      },
    );
  }, [projectId, call, read]);

  return { state, ask, put };
}

// ── The checks ───────────────────────────────────────────────────────────────

const LINE = 'flex w-full items-baseline gap-2 rounded-md px-2 py-1 text-left';
const LINE_NUMBER = 'min-w-[2.5rem] flex-shrink-0 text-right text-xl font-semibold tabular-nums';

/** One check: its number, then its name. A button only when there is a list to open. */
function CheckLine({ info, check, open, onOpen }: {
  info: HygieneCheckInfo;
  check: { status: HygieneCheckStatus; count: number | null } | undefined;
  open: boolean;
  onOpen: (slug: string) => void;
}) {
  if (!check || check.status !== 'ready' || check.count === null) {
    return (
      <p title={COULD_NOT_CHECK} className={`${LINE} text-gray-400`}>
        <span className={LINE_NUMBER} aria-label={COULD_NOT_CHECK}>—</span>
        <span className="text-sm">{info.label}</span>
      </p>
    );
  }
  if (check.count <= 0) {
    return (
      <p title={info.title} className={`${LINE} text-gray-400`}>
        <span className={LINE_NUMBER}>0</span>
        <span className="text-sm">{info.label}</span>
      </p>
    );
  }
  return (
    <button
      type="button"
      title={info.title}
      aria-pressed={open}
      onClick={() => onOpen(info.slug)}
      className={`${LINE} transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
        open ? 'bg-primary-50 text-primary-700' : 'text-gray-900 hover:bg-gray-50'
      }`}
    >
      <span className={LINE_NUMBER}>{fmtInt(check.count)}</span>
      <span className="text-sm">{info.label}</span>
    </button>
  );
}

/**
 * One side of the tab: its checks in their boxes, then the open check's list.
 * While the side loads it is a skeleton; when it fails it is one line and a way to try again.
 */
function SideBody<Info extends HygieneCheckInfo, Check extends { id: string; status: HygieneCheckStatus; count: number | null }>({
  side, state, infos, inAddress, onOpen, onRetry, list, marked,
}: {
  side: HygieneSide;
  state: SideState<{ checks: Check[] }>;
  infos: readonly Info[];
  /** The check ?list= names, on whichever side. */
  inAddress: HygieneCheckInfo | null;
  onOpen: (slug: string) => void;
  onRetry: () => void;
  list: (info: Info, check: Check) => ReactNode;
  /** The Marked OK list, when ?list= opens it in place of a check. */
  marked: ReactNode | null;
}) {
  if (state.status === 'idle' || state.status === 'loading') {
    return (
      <div role="status" aria-label="Loading" aria-busy="true" className="space-y-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {HYGIENE_GROUPS.map((g) => <div key={g.id} className="cp-shimmer h-32 rounded-2xl" />)}
        </div>
        <div className="cp-shimmer h-64 rounded-2xl" />
      </div>
    );
  }

  if (state.status !== 'ready') {
    return (
      <div role="alert" className={`${CARD} flex flex-wrap items-center gap-3 text-sm text-gray-600`}>
        <span>{side === 'stores' ? 'The store checks could not be loaded.' : 'The employee checks could not be loaded.'}</span>
        <button type="button" onClick={onRetry} className={PILL}>Try again</button>
      </div>
    );
  }

  const { checks } = state.data;
  const shown = marked ? null : shownCheck(infos, checks, inAddress);
  const shownResult = shown ? checks.find((c) => c.id === shown.id) : undefined;

  return (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {HYGIENE_GROUPS.map((group) => {
          const lines = infos.filter((i) => i.group === group.id);
          if (lines.length === 0) return null;
          return (
            <section key={group.id} aria-label={group.label} className={CARD}>
              <h3 className="text-sm font-medium text-gray-500">{group.label}</h3>
              <ul className="mt-2 space-y-0.5">
                {lines.map((info) => (
                  <li key={info.slug}>
                    <CheckLine
                      info={info}
                      check={checks.find((c) => c.id === info.id)}
                      open={shown?.slug === info.slug}
                      onOpen={onOpen}
                    />
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {marked ?? (shown && shownResult
        ? list(shown, shownResult)
        : <p className={`${CARD} text-sm text-gray-600`}>{nothingToFixLine(side, checks)}</p>)}
    </>
  );
}

// ── The open list ────────────────────────────────────────────────────────────

/** What every list has around its table: name, count, search, Download, chips, Show all. */
function ListCard({
  info, line, search, onSearch, placeholder, onDownload, canDownload, fieldCounts, chip, onChip, hidden, total, onShowAll, children,
}: {
  info: HygieneCheckInfo;
  /** The count in plain words. */
  line: string;
  search: string;
  onSearch: (text: string) => void;
  placeholder: string;
  onDownload: () => void;
  canDownload: boolean;
  fieldCounts: HygieneFieldCount[] | undefined;
  chip: string | null;
  onChip: (label: string | null) => void;
  /** Rows that match but are not drawn yet. */
  hidden: number;
  /** Rows that match. */
  total: number;
  onShowAll: () => void;
  children: ReactNode;
}) {
  return (
    <section aria-label={info.label} className={`${CARD} space-y-3`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{info.label}</h3>
          <p className="mt-0.5 text-xs text-gray-500">{line}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-64 max-w-full">
            <SearchBox value={search} onChange={onSearch} placeholder={placeholder} />
          </div>
          <button
            type="button"
            onClick={onDownload}
            disabled={!canDownload}
            title="Save these rows as an Excel file"
            className={PILL}
          >
            <Download className="h-3.5 w-3.5" /> Download
          </button>
        </div>
      </div>

      {fieldCounts && fieldCounts.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {fieldCounts.map((f) => {
            const on = chip === f.label;
            return (
              <button
                key={f.label}
                type="button"
                aria-pressed={on}
                // A second press on the chip that is on clears it.
                onClick={() => onChip(on ? null : f.label)}
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
                  on ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200'
                }`}
              >
                {f.label} <span className="font-semibold tabular-nums">{fmtInt(f.count)}</span>
              </button>
            );
          })}
        </div>
      )}

      {children}

      {/* A long list draws its first rows only: thousands of rows at once would stall
          the page. The Download holds every matching row either way. */}
      {hidden > 0 && (
        <div className="text-center">
          <button type="button" onClick={onShowAll} className={PILL}>Show all {fmtInt(total)}</button>
        </div>
      )}
    </section>
  );
}

type StoreLine = { block: StoreBlock } | { row: HygieneStoreRow };

/**
 * A store check's list.
 *
 * Mounted with a key of project + check, so the search, the chip and "Show all" start
 * fresh when another check is opened.
 *
 * The header row and the body rows list their cells in the SAME order with the same
 * condition on Status. That is deliberate: the table-actions source checker finds the
 * Actions column by position.
 */
function StoreList({ info, check, page, onMark }: {
  info: StoreCheckInfo;
  check: HygieneStoreCheck;
  /** The Stores page, when this person holds it. Without it a row has no link. */
  page: MenuItem | null;
  /** Ask to mark this row OK (the tab confirms first). */
  onMark: (row: HygieneStoreRow) => void;
}) {
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const chipOn = activeChip(chip, check.fieldCounts);
  const matching = useMemo(
    () => check.rows.filter((r) => missesField(r, chipOn) && storeMatchesSearch(info.id, r, search)),
    [check.rows, chipOn, info.id, search],
  );
  // A duplicate check is drawn block by block: the stores that share one value together.
  const blocks = useMemo(
    () => (isDuplicateCheck(info.id) ? storeBlocks(matching, check.rows) : null),
    [info.id, matching, check.rows],
  );
  // Every matching row, in the order it is drawn. This is what the Download holds.
  const ordered = useMemo(() => (blocks ? blocks.flatMap((b) => b.rows) : matching), [blocks, matching]);

  const lines: StoreLine[] = useMemo(() => {
    if (!blocks) return (showAll ? ordered : ordered.slice(0, ROW_LIMIT)).map((row) => ({ row }));
    return (showAll ? blocks : firstBlocks(blocks)).flatMap((block): StoreLine[] => [{ block }, ...block.rows.map((row) => ({ row }))]);
  }, [blocks, ordered, showAll]);
  const drawn = lines.reduce((n, line) => n + ('row' in line ? 1 : 0), 0);

  const status = showsStatus(info.id);
  // A duplicate list is headed, block by block, with what its stores share. A column
  // saying "Same name" on every row under that would only repeat the list's own name.
  const wrong = !isDuplicateCheck(info.id);
  const colCount = 4 + (wrong ? 1 : 0) + (status ? 1 : 0);

  return (
    <ListCard
      info={info}
      line={countLine(info, check, matching.length)}
      search={search}
      onSearch={setSearch}
      placeholder="Search name, code, city…"
      onDownload={() => { void downloadTableXlsx(hygieneFileName(info), storeSheet(info, ordered)); }}
      canDownload={ordered.length > 0}
      fieldCounts={check.fieldCounts}
      chip={chipOn}
      onChip={setChip}
      hidden={ordered.length - drawn}
      total={ordered.length}
      onShowAll={() => setShowAll(true)}
    >
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>Store</th>
              <th className={TH}>Category &amp; City</th>
              <th className={TH}>State</th>
              {wrong && <th className={TH}>What is wrong</th>}
              {status && <th className={TH}>Status</th>}
              <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {lines.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-8 text-center text-sm text-gray-500">No store matches.</td></tr>
            ) : lines.map((line) => ('block' in line ? (
              <tr key={`block:${line.block.key}`} className="bg-primary-50/60">
                <td colSpan={colCount} className="px-4 py-2 text-xs font-semibold text-gray-800">
                  {blockHeading(line.block)}
                  {info.id === 'same-name' && line.block.sameCity && (
                    <span
                      title="At least two of these stores are in the same city"
                      className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800"
                    >
                      same city
                    </span>
                  )}
                </td>
              </tr>
            ) : (
              <tr key={`${line.row.groupKey ?? ''}:${line.row.storeId}`} className="hover:bg-gray-50">
                <td className={CELL}>
                  <div className="font-medium text-gray-900">{line.row.storeName || '—'}</div>
                  <div className="text-xs text-gray-500">Store Code: {line.row.storeCode || '—'}</div>
                </td>
                <td className={CELL}>
                  <div className="text-gray-700">{line.row.category || '—'}</div>
                  <div className="text-xs text-gray-500">{line.row.city || '—'}</div>
                </td>
                <td className={TD}>{line.row.state || '—'}</td>
                {wrong && <td className={TD}>{storeWrongText(info.id, line.row)}</td>}
                {status && <td className={TD}>{line.row.status || '—'}</td>}
                <td className="px-4 py-3 text-center">
                  <TableActionGroup>
                    {/* Opens in a new tab, so this list keeps its place. Drawn only for
                        someone who holds the Stores page: a link to a page the menu does
                        not give them would land on "no access". */}
                    {page && (
                      <TableActionLink
                        action="open"
                        label={`Open ${line.row.storeName || line.row.storeCode} in ${page.name}`}
                        href={storeFixLink(line.row)}
                        target="_blank"
                      />
                    )}
                    {/* Anyone who sees this list may mark a row OK: the API asks the
                        same question of them as for the list itself. */}
                    <TableActionButton
                      action="approve"
                      label={`Mark ${line.row.storeName || line.row.storeCode} as OK`}
                      title={MARK_HOVER}
                      onClick={() => onMark(line.row)}
                    />
                  </TableActionGroup>
                </td>
              </tr>
            )))}
          </tbody>
        </table>
      </div>
    </ListCard>
  );
}

/** An employee check's list. Keyed like StoreList, and its rows are laid out the same way. */
function EmployeeList({ info, check, page, onMark }: {
  info: EmployeeCheckInfo;
  check: HygieneEmployeeCheck;
  /** The page this check is fixed on (Employees, or User Role Assignment), when this person holds it. */
  page: MenuItem | null;
  /** Ask to mark this row OK (the tab confirms first). */
  onMark: (row: HygieneEmployeeRow) => void;
}) {
  const [search, setSearch] = useState('');
  const [chip, setChip] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const chipOn = activeChip(chip, check.fieldCounts);
  const matching: HygieneEmployeeRow[] = useMemo(
    () => check.rows.filter((r) => missesField(r, chipOn) && employeeMatchesSearch(info.id, r, search)),
    [check.rows, chipOn, info.id, search],
  );
  const rows = showAll ? matching : matching.slice(0, ROW_LIMIT);
  // A role is fixed on User Role Assignment; anything else about a person on Employees.
  const roleFix = employeeFixPage(info.id) === USER_ROLES_PAGE;

  return (
    <ListCard
      info={info}
      line={countLine(info, check, matching.length)}
      search={search}
      onSearch={setSearch}
      placeholder="Search name, code, role…"
      onDownload={() => { void downloadTableXlsx(hygieneFileName(info), employeeSheet(info, matching)); }}
      canDownload={matching.length > 0}
      fieldCounts={check.fieldCounts}
      chip={chipOn}
      onChip={setChip}
      hidden={matching.length - rows.length}
      total={matching.length}
      onShowAll={() => setShowAll(true)}
    >
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>Employee</th>
              <th className={TH}>Role</th>
              <th className={TH}>What is wrong</th>
              <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">No person matches.</td></tr>
            ) : rows.map((row) => (
              <tr key={row.employeeCode} className="hover:bg-gray-50">
                <td className={CELL}>
                  <div className="font-medium text-gray-900">{row.employeeName || '—'}</div>
                  <div className="text-xs text-gray-500">{row.employeeCode}</div>
                </td>
                <td className={TD}>{row.roleName || '—'}</td>
                <td className={TD}>{employeeWrongText(info.id, row)}</td>
                <td className="px-4 py-3 text-center">
                  <TableActionGroup>
                    {/* One link, by where the fix is made: a key for a role, people for
                        the person's own details. Drawn only when that page is held. */}
                    {page && (roleFix ? (
                      <TableActionLink
                        action="access"
                        // Someone with no role is not on that page's list yet: the
                        // page opens whole, and the role is given from there.
                        label={info.id === 'no-role'
                          ? `Give ${row.employeeName || row.employeeCode} a role in ${page.name}`
                          : `Open ${row.employeeName || row.employeeCode} in ${page.name}`}
                        href={employeeFixLink(info.id, row)}
                        target="_blank"
                      />
                    ) : (
                      <TableActionLink
                        action="candidates"
                        label={`Open ${row.employeeName || row.employeeCode} in ${page.name}`}
                        href={employeeFixLink(info.id, row)}
                        target="_blank"
                      />
                    ))}
                    <TableActionButton
                      action="approve"
                      label={`Mark ${row.employeeName || row.employeeCode} as OK`}
                      title={MARK_HOVER}
                      onClick={() => onMark(row)}
                    />
                  </TableActionGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ListCard>
  );
}

// ── Marked OK ────────────────────────────────────────────────────────────────

/** One row of the Marked OK list, whichever side it is from. */
interface MarkedLine {
  key: string;
  name: string;
  /** Under the name: the store code, or the employee code. */
  code: string;
  checkLabel: string;
  /** Under the check: what was flagged, when it adds to the check's name. */
  detail: string;
  markedAt: string;
  markedBy: string;
  request: MarkRequest;
}

const STORE_INFO = new Map(STORE_CHECKS.map((info) => [info.id, info]));
const EMPLOYEE_INFO = new Map(EMPLOYEE_CHECKS.map((info) => [info.id, info]));

const markedStoreLine = (entry: HygieneMarkedStore): MarkedLine => {
  const info = STORE_INFO.get(entry.check);
  const label = info?.label ?? entry.check;
  // A duplicate check's row is about what the stores shared: its name, point or code.
  // A shared name is the store's own name, already on the row, so it is not said twice.
  const detail = isDuplicateCheck(entry.check) ? entry.row.groupLabel ?? '' : storeWrongText(entry.check, entry.row);
  const said = [label, entry.row.storeName].map((text) => text.trim().toLowerCase());
  return {
    key: `${entry.check}:${entry.row.storeId}`,
    name: entry.row.storeName || '—',
    code: `Store Code: ${entry.row.storeCode || '—'}`,
    checkLabel: label,
    detail: said.includes(detail.trim().toLowerCase()) ? '' : detail,
    markedAt: entry.markedAt,
    markedBy: entry.markedBy,
    request: storeMarkRequest(entry.check, entry.row),
  };
};

const markedEmployeeLine = (entry: HygieneMarkedEmployee): MarkedLine => {
  const label = EMPLOYEE_INFO.get(entry.check)?.label ?? entry.check;
  const detail = employeeWrongText(entry.check, entry.row);
  return {
    key: `${entry.check}:${entry.row.employeeCode}`,
    name: entry.row.employeeName || entry.row.employeeCode,
    code: entry.row.employeeCode,
    checkLabel: label,
    detail: detail === label ? '' : detail,
    markedAt: entry.markedAt,
    markedBy: entry.markedBy,
    request: employeeMarkRequest(entry.check, entry.row),
  };
};

/** The rows marked OK on one side, newest first, each with Undo. */
function MarkedList({ side, lines, undoing, error, onUndo }: {
  side: HygieneSide;
  lines: MarkedLine[];
  /** The row whose undo is on its way. */
  undoing: string | null;
  error: string | null;
  onUndo: (line: MarkedLine) => void;
}) {
  const names = HYGIENE_SIDES.find((s) => s.id === side) ?? HYGIENE_SIDES[0];
  return (
    <section aria-label={MARKED_LABEL} className={`${CARD} space-y-3`}>
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{MARKED_LABEL}</h3>
        <p className="mt-0.5 text-xs text-gray-500">{fmtInt(lines.length)} {lines.length === 1 ? names.one : names.many}</p>
      </div>
      {error && <FormErrorBanner message={error} />}
      <div className="overflow-x-auto rounded-xl border border-gray-100">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>{side === 'stores' ? 'Store' : 'Employee'}</th>
              <th className={TH}>Check</th>
              <th className={TH}>Marked</th>
              <th className="whitespace-nowrap px-4 py-3 text-center text-xs font-semibold text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {lines.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">Nothing is marked OK.</td></tr>
            ) : lines.map((line) => (
              <tr key={line.key} className="hover:bg-gray-50">
                <td className={CELL}>
                  <div className="font-medium text-gray-900">{line.name}</div>
                  <div className="text-xs text-gray-500">{line.code}</div>
                </td>
                <td className={CELL}>
                  <div className="text-gray-700">{line.checkLabel}</div>
                  {line.detail && <div className="text-xs text-gray-500">{line.detail}</div>}
                </td>
                <td className={CELL}>
                  <div className="text-gray-700">{fmtMarkedDate(line.markedAt) || '—'}</div>
                  {line.markedBy && <div className="text-xs text-gray-500">by {line.markedBy}</div>}
                </td>
                <td className="px-4 py-3 text-center">
                  <TableActionGroup>
                    <TableActionButton
                      action="restore"
                      label={`Undo Mark as OK for ${line.name}`}
                      title="Undo: list it again"
                      loading={undoing === line.key}
                      disabled={undoing !== null && undoing !== line.key}
                      onClick={() => onUndo(line)}
                    />
                  </TableActionGroup>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/** The in-page confirm before a row is marked OK. */
function MarkConfirm({ pending, saving, error, onCancel, onConfirm }: {
  pending: { name: string; checkLabel: string } | null;
  saving: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!pending) return null;
  return (
    <Modal
      isOpen
      // A save in flight is let finish: closing now would hide whether it went through.
      onClose={() => { if (!saving) onCancel(); }}
      title={MARK_QUESTION}
      size="sm"
      footer={(
        <>
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={saving}
            title={MARK_HOVER}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Mark as OK
          </button>
        </>
      )}
    >
      <div className="space-y-3">
        <div>
          <p className="text-sm font-medium text-gray-900">{pending.name}</p>
          <p className="text-xs text-gray-500">{pending.checkLabel}</p>
        </div>
        <p className="text-sm text-gray-700">{MARK_ANSWER}</p>
        {error && <FormErrorBanner message={error} />}
      </div>
    </Modal>
  );
}

// ── The tab ──────────────────────────────────────────────────────────────────

const NO_ROLES: readonly string[] = [];

export default function DataHygieneTab({ projectId, roles = NO_ROLES, rolesPending = false }: {
  projectId: number | null;
  /** The page's Role filter: role names. Empty = everyone. Narrows the Employees side only. */
  roles?: readonly string[];
  /** The page is still working out the store-level Role default: the Employees side waits for it. */
  rolesPending?: boolean;
}) {
  const { list, openList } = useDashboardList();
  const inAddress = checkFromSlug(list);
  // The Marked OK list of a side, when ?list= opens it.
  const markedSide = inAddress ? null : markedSideFromSlug(list);
  const side: HygieneSide = inAddress?.side ?? markedSide ?? 'stores';

  const stores = useSide(projectId, askStores, readStores);
  const employees = useSide(projectId, askEmployees, readEmployees);

  // The Employees side as it is drawn: narrowed to the chosen roles. Until the page has
  // its Role default it reads as loading, so nobody sees everyone's figures first and
  // then fewer. A failure or a refusal does not depend on the roles and shows at once.
  const employeesState = employees.state;
  const employeesShown = useMemo((): SideState<DataHygieneEmployees> => {
    if (employeesState.status !== 'ready') return employeesState;
    if (rolesPending) return LOADING;
    return { ...employeesState, data: employeesInRoles(employeesState.data, roles) };
  }, [employeesState, roles, rolesPending]);
  const openState = side === 'stores' ? stores.state : employeesShown;

  // Each side is asked for whenever it holds nothing: on arrival and after a project
  // change. A side that failed is not asked again until someone presses Try again or
  // Refresh.
  const storesStatus = stores.state.status;
  const askStoresSide = stores.ask;
  useEffect(() => {
    if (storesStatus === 'idle') askStoresSide();
  }, [storesStatus, askStoresSide]);
  const employeesStatus = employees.state.status;
  const askEmployeesSide = employees.ask;
  useEffect(() => {
    if (employeesStatus === 'idle') askEmployeesSide();
  }, [employeesStatus, askEmployeesSide]);

  // A row's link is drawn only for someone who holds the page it opens, the way the
  // "Related" links under every dashboard are.
  const storesPage = useHeldPage(STORES_PAGE);
  const employeesPage = useHeldPage(EMPLOYEES_PAGE);
  const rolesPage = useHeldPage(USER_ROLES_PAGE);

  // "Mark as OK": the row waiting on the confirm, and its save.
  const [pending, setPending] = useState<{ request: MarkRequest; name: string; checkLabel: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [markError, setMarkError] = useState<string | null>(null);
  // Undo, from the Marked OK list.
  const [undoing, setUndoing] = useState<string | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);

  if (projectId == null) {
    return <div className="flex h-40 items-center justify-center text-sm text-gray-500">Select a project to view data hygiene.</div>;
  }

  // Both sides again, each on its own request.
  const refresh = () => {
    stores.ask();
    employees.ask();
  };

  /** Hold the side a mark or undo answered with; when it cannot be read, ask for that side again. */
  const hold = (which: HygieneSide, raw: unknown) => {
    const held = which === 'stores' ? stores : employees;
    if (!held.put(projectId, raw)) held.ask();
  };

  const askToMark = (request: MarkRequest, name: string, checkLabel: string) => {
    setMarkError(null);
    setPending({ request, name, checkLabel });
  };

  const confirmMark = async () => {
    if (!pending || saving) return;
    setSaving(true);
    setMarkError(null);
    try {
      const res = await dataHygieneDashboardAPI.markOk(projectId, pending.request);
      hold(pending.request.side, (res as Answer)?.data?.data);
      setPending(null);
    } catch (err) {
      setMarkError(markErrorText(err));
    } finally {
      setSaving(false);
    }
  };

  const undoMark = async (line: MarkedLine) => {
    if (undoing) return;
    setUndoing(line.key);
    setUndoError(null);
    try {
      const res = await dataHygieneDashboardAPI.undoMark(projectId, line.request);
      hold(line.request.side, (res as Answer)?.data?.data);
    } catch (err) {
      setUndoError(markErrorText(err, true));
    } finally {
      setUndoing(null);
    }
  };

  // Both sides sit behind the same rule, so a refusal of one is a refusal of the tab.
  if (stores.state.status === 'denied' || employees.state.status === 'denied') {
    return (
      <div role="status" className="rounded-xl border border-gray-200 bg-white px-5 py-6 text-sm text-gray-700">
        <p>{ACCESS_NEEDED}</p>
        {/* Access takes effect on the next request, so the person who has just been
            given it can find out without leaving the dashboard. */}
        <button type="button" onClick={refresh} className="mt-3 text-sm font-medium text-primary-600 hover:underline">
          Check again
        </button>
      </div>
    );
  }

  // Either side on its way keeps Refresh waiting: pressed again meanwhile, it would
  // send the slow read of the people a second time beside the first.
  const onItsWay = (state: { status: string; refreshing?: boolean }) => state.status === 'loading' || (state.status === 'ready' && state.refreshing === true);
  const busy = onItsWay(stores.state) || onItsWay(employees.state);
  const checked = openState.status === 'ready' ? fmtCheckedTime(openState.data.asOf) : null;
  // Which figures the Role filter narrows, in a few words, while a role is chosen.
  const note = side === 'employees' && rolesPending ? null : roleFilterNote(side, roles);

  /**
   * How many stores or people have something to fix; null while that is not known.
   * A 0 with a check that could not be run is not known either: that check may hold the
   * only ones to fix, and "(0)" would say nobody needs anything. Above 0 the number
   * stands, with that check's dash beside it.
   */
  const issues = (which: HygieneSide): number | null => {
    const known = (count: number, checks: ReadonlyArray<{ status: HygieneCheckStatus }>): number | null =>
      (count === 0 && checks.some((check) => check.status !== 'ready') ? null : count);
    if (which === 'stores') return stores.state.status === 'ready' ? known(stores.state.data.storesWithIssues, stores.state.data.checks) : null;
    return employeesShown.status === 'ready' ? known(employeesShown.data.peopleWithIssues, employeesShown.data.checks) : null;
  };

  /** The Marked OK list of a side, as drawn: null until that side has loaded. */
  const markedLines = (which: HygieneSide): MarkedLine[] | null => {
    if (which === 'stores') return stores.state.status === 'ready' ? stores.state.data.marked.map(markedStoreLine) : null;
    return employeesShown.status === 'ready' ? employeesShown.data.marked.map(markedEmployeeLine) : null;
  };
  const sideMarked = markedLines(side);
  const markedOpen = markedSide === side;
  /** The side's first check with something in it, else its first check. */
  const firstSlug = (which: HygieneSide): string => {
    const state = which === 'stores' ? stores.state : employeesShown;
    const first = state.status === 'ready' ? shownCheck(checksOf(which), state.data.checks, null) : null;
    return (first ?? checksOf(which)[0]).slug;
  };
  const markedList = markedOpen && sideMarked
    ? <MarkedList side={side} lines={sideMarked} undoing={undoing} error={undoError} onUndo={(line) => { void undoMark(line); }} />
    : null;

  const openSide = (to: HygieneSide) => {
    if (to === side && !markedOpen) return;
    // Straight to its first check with something in it when that is already known,
    // so the address names the list that is drawn.
    openList(firstSlug(to));
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
        <div role="tablist" aria-label="Stores or employees" className="flex w-fit flex-wrap items-center gap-1 rounded-lg border border-gray-200 bg-gray-100 p-1">
          {HYGIENE_SIDES.map((s) => {
            const Icon = SIDE_ICON[s.id];
            const on = s.id === side;
            const count = issues(s.id);
            return (
              <button
                key={s.id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => openSide(s.id)}
                className={`flex items-center gap-1.5 rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${
                  on ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                }`}
              >
                <Icon className="h-4 w-4" aria-hidden />
                {s.label}{count !== null ? ` (${fmtInt(count)})` : ''}
              </button>
            );
          })}
        </div>
        {/* The rows marked OK on this side, with Undo. Shown once there is one, and while
            its list is open, so the last Undo does not take the way back away. */}
        {sideMarked && (sideMarked.length > 0 || markedOpen) && (
          <button
            type="button"
            aria-pressed={markedOpen}
            onClick={() => openList(markedOpen ? firstSlug(side) : MARKED_SLUG[side])}
            title={markedOpen ? 'Back to the checks' : 'Rows marked OK: they are not flagged again'}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ${
              markedOpen ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 bg-white text-gray-600 hover:border-primary-200 hover:text-primary-700'
            }`}
          >
            <CircleCheck className="h-3.5 w-3.5" aria-hidden />
            {MARKED_LABEL} ({fmtInt(sideMarked.length)})
          </button>
        )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {note && <span title={note.title} className="text-xs text-gray-500">{note.text}</span>}
          {checked && <span className="text-xs text-gray-500">Checked {checked}</span>}
          <button type="button" onClick={refresh} disabled={busy} title="Run the checks again" className={PILL}>
            <RefreshCw className={`h-3.5 w-3.5 ${busy ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {side === 'stores' ? (
        <SideBody
          side="stores"
          state={stores.state}
          infos={STORE_CHECKS}
          inAddress={inAddress}
          onOpen={openList}
          onRetry={stores.ask}
          marked={markedList}
          list={(info, check: HygieneStoreCheck) => (
            <StoreList
              key={`${projectId}:${info.slug}`}
              info={info}
              check={check}
              page={storesPage}
              onMark={(row) => askToMark(storeMarkRequest(info.id, row), row.storeName || row.storeCode, info.label)}
            />
          )}
        />
      ) : (
        <SideBody
          side="employees"
          state={employeesShown}
          infos={EMPLOYEE_CHECKS}
          inAddress={inAddress}
          onOpen={openList}
          onRetry={employees.ask}
          marked={markedList}
          list={(info, check: HygieneEmployeeCheck) => (
            <EmployeeList
              key={`${projectId}:${info.slug}`}
              info={info}
              check={check}
              page={employeeFixPage(info.id) === USER_ROLES_PAGE ? rolesPage : employeesPage}
              onMark={(row) => askToMark(employeeMarkRequest(info.id, row), row.employeeName || row.employeeCode, info.label)}
            />
          )}
        />
      )}

      <MarkConfirm
        pending={pending}
        saving={saving}
        error={markError}
        onCancel={() => setPending(null)}
        onConfirm={() => { void confirmMark(); }}
      />
    </div>
  );
}
